// ⌘K (Ctrl+K): one box that finds a lead by name, phone or #number, a client, a website, a page, or something to do.
// Centered on a computer, the whole screen on a phone. Arrow keys move, Enter opens, Esc closes.
import {Fragment,useEffect,useId,useMemo,useRef,useState} from 'react';
import {ArrowLeft,CornerDownLeft,Search} from 'lucide-react';
import {supabase} from './supabase';
import {portalError} from './portal-model';
import type {Viewer} from './nav-model';
import type {Go} from './page-link';
import {actionHits,pageHits,resultHits,searchable,serverQuery,type Hit,type SearchResult} from './command-model';
import './command-search.css';

export function CommandSearch({open,onClose,who,go}:{open:boolean;onClose:()=>void;who:Viewer;go:Go}){
 const [query,setQuery]=useState(''),[result,setResult]=useState<SearchResult|null>(null),[busy,setBusy]=useState(false),[error,setError]=useState('');
 const [active,setActive]=useState(0);
 const input=useRef<HTMLInputElement>(null),list=useRef<HTMLUListElement>(null),sequence=useRef(0),returnTo=useRef<Element|null>(null);
 const listId=useId();
 useEffect(()=>{
  if(!open)return;
  returnTo.current=document.activeElement;setQuery('');setResult(null);setError('');setActive(0);
  const t=window.setTimeout(()=>input.current?.focus(),0);
  const scroll=document.body.style.overflow;document.body.style.overflow='hidden';
  return()=>{window.clearTimeout(t);document.body.style.overflow=scroll;(returnTo.current as HTMLElement|null)?.focus?.({preventScroll:true});};
 },[open]);
 useEffect(()=>{
  const n=++sequence.current;
  if(!open||!searchable(query)){setBusy(false);return;}
  setBusy(true);
  const t=window.setTimeout(()=>{
   void Promise.resolve(supabase.rpc('dashboard_search',{p_query:serverQuery(query)})).then(r=>{
    if(n!==sequence.current)return;
    setBusy(false);
    if(r.error)setError(portalError(r.error));else{setError('');setResult(r.data as SearchResult);}
   });
  },220);
  return()=>window.clearTimeout(t);
 },[query,open]);
 const hits=useMemo(()=>{
  const server=result&&searchable(query)&&result.query===serverQuery(query)?resultHits(result,who):[];
  return [...pageHits(query,who),...actionHits(query,who),...server];
 },[query,result,who]);
 useEffect(()=>{setActive(0);},[hits.length,query]);
 useEffect(()=>{list.current?.querySelector('[aria-selected="true"]')?.scrollIntoView({block:'nearest'});},[active]);
 if(!open)return null;
 const choose=(hit:Hit|undefined)=>{if(!hit)return;onClose();go(hit.to,hit.params);};
 const onKey=(e:React.KeyboardEvent)=>{
  if(e.key==='ArrowDown'){e.preventDefault();setActive(a=>Math.min(a+1,hits.length-1));}
  else if(e.key==='ArrowUp'){e.preventDefault();setActive(a=>Math.max(a-1,0));}
  else if(e.key==='Enter'){e.preventDefault();choose(hits[active]);}
  else if(e.key==='Escape'){e.preventDefault();onClose();}
 };
 const waiting=searchable(query)&&(busy||!result||result.query!==serverQuery(query));
 return <div className="cmdk" role="presentation" onMouseDown={e=>{if(e.target===e.currentTarget)onClose();}}>
  <div className="cmdk__panel" role="dialog" aria-modal="true" aria-label="Search">
   <div className="cmdk__field">
    <button type="button" className="cmdk__back" aria-label="Close search" onClick={onClose}><ArrowLeft size={18}/></button>
    <Search size={18} className="cmdk__icon" aria-hidden="true"/>
    <input ref={input} role="combobox" aria-expanded="true" aria-controls={listId} aria-autocomplete="list" aria-activedescendant={hits[active]?`${listId}-${active}`:undefined}
     aria-label="Search leads, clients, websites and pages" placeholder="Lead name, phone or #number, a client, a website…" value={query} spellCheck={false} autoComplete="off"
     onChange={e=>setQuery(e.target.value)} onKeyDown={onKey}/>
    <kbd className="cmdk__esc">Esc</kbd>
   </div>
   <ul className="cmdk__list" id={listId} role="listbox" ref={list} aria-label="Results">
    {hits.map((h,i)=><Fragment key={h.key}>
     {(i===0||hits[i-1].group!==h.group)&&<li role="presentation" className="cmdk__group">{h.group}</li>}
     <li id={`${listId}-${i}`} role="option" aria-selected={i===active} className="cmdk__hit" onMouseMove={()=>setActive(i)} onClick={()=>choose(h)}>
      <span className="cmdk__text"><strong>{h.label}</strong>{h.detail&&<small>{h.detail}</small>}</span>
      {i===active&&<CornerDownLeft size={15} className="cmdk__enter" aria-hidden="true"/>}
     </li>
    </Fragment>)}
   </ul>
   <p className="cmdk__status" role="status">{error?error:waiting?'Searching leads, clients and websites…':query.trim()&&!hits.length?`Nothing matches “${query.trim()}”.`:!query.trim()?'Find a lead by name, phone or #number, a client, a website, a page or something to do.':''}</p>
   <footer className="cmdk__foot" aria-hidden="true"><span><kbd>↑</kbd><kbd>↓</kbd> move</span><span><kbd>Enter</kbd> open</span><span><kbd>Esc</kbd> close</span></footer>
  </div>
 </div>;
}
